import React from 'react';
import InfoText from '@/components/atoms/InfoText';
import { MagicStar, ShoppingBag, Timer, UserOctagon } from 'iconsax-react';
import { Image } from 'antd';
import type { PostDetailsCardTranslator } from './postDetailsCardTranslations';

export default function BasicProfileCard({
  name,
  photo,
  rate,
  reviews,
  orders,
  avgResponse,
  tag,
  t,
}: {
  profileId?: string;
  name?: string;
  photo?: string;
  rate?: string | number | null;
  reviews?: string | number | null;
  orders?: string | number | null;
  avgResponse?: string | null;
  tag?: string;
  tags?: { name: string; code: string }[];
  actions?: React.ReactElement;
  t: PostDetailsCardTranslator;
  canProfileRoute?: boolean;
}) {
  const getLocalizedTag = (tagText: string) => {
    const lower = tagText?.trim().toLowerCase();
    if (lower === 'invited') return t('postDetails.tagInvited');
    if (lower === 'negotiation') return t('postDetails.tagNegotiation');
    return tagText;
  };

  const displayName = name || '-';

  const { ratingDisplay, ordersDisplay, formattedAvgResponse } = React.useMemo(() => {
    const numRate = Number(rate ?? 0);
    const numReviews = Number(reviews ?? 0);
    const numOrders = Number(orders ?? 0);

    const rating =
      !rate || (numRate === 0 && numReviews === 0)
        ? t('postDetails.zeroReviews')
        : `${rate}${reviews !== undefined && reviews !== null ? ` (${reviews} ${t('postDetails.reviews')})` : ''}`;

    const orderText =
      !orders || numOrders === 0
        ? t('postDetails.zeroOrder')
        : `${numOrders}+ ${t('postDetails.orders')}`;

    let avgResp: string | null = null;
    if (avgResponse) {
      const str = String(avgResponse).trim();
      const timeStr = /^\d+$/.test(str) ? `${str}hrs` : str.replace(/\s*response$/i, '');
      avgResp = t('postDetails.avgResponse', { time: timeStr });
    }

    return {
      ratingDisplay: rating,
      ordersDisplay: orderText,
      formattedAvgResponse: avgResp,
    };
  }, [rate, reviews, orders, avgResponse, t]);

  const isInvited = tag?.trim().toLowerCase() === 'invited';

  return (
    <div
      className="flex items-center justify-between gap-4 flex-wrap sm:flex-nowrap"
      data-testid="basic-profile-card"
    >
      {/* Left Group: Avatar + Details (Name & Stats) */}
      <div className="flex items-center gap-3.5 min-w-0" data-testid="basic-profile-avatar-details-group">
        {/* Avatar */}
        <div
          className={`w-[56px] h-[56px] sm:w-[60px] sm:h-[60px] rounded-full overflow-hidden shrink-0 bg-light-surface-secondary dark:bg-dark-surface-secondary flex items-center justify-center ${
            isInvited
              ? 'border-2 border-light-success dark:border-dark-success'
              : 'border border-light-input-border dark:border-dark-input-border'
          } [&_.ant-image]:!w-full [&_.ant-image]:!h-full [&_.ant-image-img]:!w-full [&_.ant-image-img]:!h-full [&_.ant-image-img]:!object-cover select-none`}
          data-testid="basic-profile-avatar"
        >
          {photo ? (
            <Image
              src={photo}
              alt={displayName}
              className="!w-full !h-full !object-cover"
              preview={false}
            />
          ) : (
            <div
              className="w-full h-full flex items-center justify-center font-bold text-light-text-primary dark:text-dark-text-primary text-[20px]"
              data-testid="basic-profile-avatar-initial"
            >
              {displayName.charAt(0).toUpperCase()}
            </div>
          )}
        </div>

        <div
          className="flex flex-col gap-1 min-w-0"
          data-testid="basic-profile-card-details"
        >
          {/* Name */}
          <InfoText
            text={displayName}
            className="!text-[16px] sm:!text-[18px] !leading-[120%] !font-bold tracking-normal !text-light-text-primary dark:!text-dark-text-primary"
            data-testid="basic-profile-card-name"
          />

          {/* Stats Row */}
          <div
            className="flex flex-wrap items-center gap-3 sm:gap-4 text-light-text-primary dark:text-dark-text-primary text-[14px] sm:text-[15px]"
            data-testid="basic-profile-card-stats"
          >
            {/* Rating */}
            {ratingDisplay && (
              <div
                className="flex items-center gap-1.5"
                data-testid="basic-profile-card-rating"
              >
                <MagicStar size={16} className="text-light-alert dark:text-dark-alert" variant="Bold" />
                <InfoText
                  text={ratingDisplay}
                  className="!font-normal !text-[14px] sm:!text-[15px] !leading-[100%] tracking-normal !text-light-text-primary dark:!text-dark-text-primary"
                  data-testid="basic-profile-card-rating-text"
                />
              </div>
            )}

            {/* Orders */}
            {ordersDisplay && (
              <div
                className="flex items-center gap-1.5"
                data-testid="basic-profile-card-orders"
              >
                <ShoppingBag
                  size={16}
                  className="text-light-information dark:text-dark-information"
                  variant="Bold"
                />
                <InfoText
                  text={ordersDisplay}
                  className="!font-normal !text-[14px] sm:!text-[15px] !leading-[100%] tracking-normal !text-light-text-primary dark:!text-dark-text-primary"
                  data-testid="basic-profile-card-orders-text"
                />
              </div>
            )}

            {/* Avg Response */}
            {formattedAvgResponse && (
              <div
                className="flex items-center gap-1.5"
                data-testid="basic-profile-card-response"
              >
                <Timer
                  size={16}
                  className="text-light-success dark:text-dark-success"
                  variant="Bold"
                />
                <InfoText
                  text={formattedAvgResponse}
                  className="!font-normal !text-[14px] sm:!text-[15px] !leading-[100%] tracking-normal !text-light-text-primary dark:!text-dark-text-primary"
                  data-testid="basic-profile-card-response-text"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tag */}
      {tag && (
        <div
          className="h-[34px] px-3 py-1.5 inline-flex items-center gap-1.5 rounded-[8px] bg-green-50 dark:bg-green-950/40 text-light-success dark:text-dark-success shrink-0"
          data-testid="basic-profile-card-tag"
        >
          <UserOctagon size={16} variant="Bold" className="text-light-success dark:text-dark-success" />
          <InfoText
            text={getLocalizedTag(tag)}
            className="!text-[13px] !font-medium !leading-[100%] !text-light-success dark:!text-dark-success"
            data-testid="basic-profile-card-tag-text"
          />
        </div>
      )}
    </div>
  );
}
